import { useCallback, useState } from "react";
import { useApi } from "@/hooks/useApi";
import { toast } from "react-toastify";
import { apiClient } from "@/utils/api-client";

import type {
  APIResponse,
  Organization,
  OrganizationDetailResponse,
  OrganizationDetailSerializer,
  OrganizationListResponse,
  OrganizationListSerializer,
  OrganizationMembership,
  OrganizationMembershipPaginationData,
  ResponseMeta,
} from "./types";

// Organization Hook - Preserving original behavior
export const useOrganization = () => {
  const { request, isLoading, error } = useApi();
  const [organizations, setOrganizations] = useState<OrganizationListSerializer[]>([]);
  const [currentOrganization, setCurrentOrganization] = useState<OrganizationDetailSerializer | null>(null);

  const getOrganizations = useCallback(async () => {
    try {
      const response = await request<OrganizationListResponse>("/api/organizations/");
      if (response && response.data) {
        setOrganizations(response.data);
        return response;
      }
    } catch (error) {
      console.error("Failed to get organizations:", error);
      throw error;
    }
  }, [request]);

  const getOrganization = useCallback(async (organizationId: string) => {
    try {
      const response = await request<OrganizationDetailResponse>(`/api/organizations/${organizationId}/`);
      if (response && response.data) {
        setCurrentOrganization(response.data);
        return response;
      }
    } catch (error) {
      console.error("Failed to get organization:", error);
      throw error;
    }
  }, [request]);

  const createOrganization = useCallback(async (organizationData: Partial<Organization>) => {
    try {
      const formData = new FormData();
      Object.keys(organizationData).forEach((key) => {
        const value = organizationData[key as keyof Partial<Organization>];
        if (value instanceof File) {
          formData.append(key, value, value.name);
        } else if (value !== undefined && value !== null) {
          formData.append(key, String(value));
        }
      });

      const response = await request<OrganizationDetailResponse>("/api/organizations/", {
        method: "POST",
        body: organizationData.logo ? formData : JSON.stringify(organizationData),
        headers: organizationData.logo ? undefined : { 'Content-Type': 'application/json' },
      });

      if (response && response.data) {
        setOrganizations(prev =>
          prev ? [...prev, response.data] : [response.data]
        );
        return response;
      }
    } catch (error) {
      console.error("Failed to create organization:", error);
      throw error;
    }
  }, [request]);

  const updateOrganization = useCallback(async (organizationId: string, organizationData: Partial<Organization>) => {
    try {
      const response = await request<OrganizationDetailResponse>(`/api/organizations/${organizationId}/`, {
        method: "PATCH",
        body: JSON.stringify(organizationData),
      });
      if (response && response.data) {
        setCurrentOrganization(response.data);
        setOrganizations(prev =>
          prev && prev.length ? prev.map(o => o.id === organizationId ? response.data : o) : prev
        );
        return response;
      }
    } catch (error) {
      console.error("Failed to update organization:", error);
      throw error;
    }
  }, [request]);

  const uploadLogo = useCallback(async (organizationId: string, logoFile: File) => {
    try {
      // Use the apiClient's uploadFile method
      const response = await apiClient.uploadFile<any>(
        `/api/organizations/${organizationId}/upload_logo/`,
        logoFile,
        'logo',
        {}
      );

      if (response && response.data) {
        toast.success("Logo uploaded successfully");
        return response.data;
      }
    } catch (error) {
      console.error("Failed to upload logo:", error);
      toast.error("Failed to upload logo");
      throw error;
    }
  }, []);

  const inviteMember = useCallback(async (organizationId: string, email: string, role: string) => {
    try {
      const response = await request<APIResponse<any>>(`/api/organizations/${organizationId}/invite_member/`, {
        method: "POST",
        body: JSON.stringify({ email, role }),
      });

      if (response) {
        toast.success("Invitation sent successfully");
        return response;
      }
    } catch (error) {
      console.error("Failed to invite member:", error);
      toast.error("Failed to invite member");
      throw error;
    }
  }, [request]);

  const removeMember = useCallback(async (organizationId: string, userId: string) => {
    try {
      const response = await request<APIResponse<any>>(`/api/organizations/${organizationId}/remove_member/`, {
        method: "POST",
        body: JSON.stringify({ user_id: userId }),
      });

      if (response) {
        toast.success("Member removed successfully");
        return response;
      }
    } catch (error) {
      console.error("Failed to remove member:", error);
      toast.error("Failed to remove member");
      throw error;
    }
  }, [request]);

  const changeMemberRole = useCallback(async (organizationId: string, userId: string, role: string) => {
    try {
      const response = await request<APIResponse<any>>(`/api/organizations/${organizationId}/change_member_role/`, {
        method: "POST",
        body: JSON.stringify({ user_id: userId, role }),
      });

      if (response) {
        toast.success("Role updated successfully");
        return response;
      }
    } catch (error) {
      console.error("Failed to change member role:", error);
      toast.error("Failed to change member role");
      throw error;
    }
  }, [request]);

  const updateSettings = useCallback(async (organizationId: string, settings: any) => {
    try {
      const response = await request<APIResponse<any>>(`/api/organizations/${organizationId}/update_settings/`, {
        method: "PATCH",
        body: JSON.stringify(settings),
      });

      if (response) {
        toast.success("Settings updated successfully");
        return response;
      }
    } catch (error) {
      console.error("Failed to update settings:", error);
      toast.error("Failed to update settings");
      throw error;
    }
  }, [request]);

  return {
    organizations,
    currentOrganization,
    isLoading,
    error,
    getOrganizations,
    getOrganization,
    createOrganization,
    updateOrganization,
    uploadLogo,
    inviteMember,
    removeMember,
    changeMemberRole,
    updateSettings,
  };
};

// Organization Membership Hook - Keeping the same behavior as original
export const useOrganizationMembership = () => {
  const { request, isLoading, error } = useApi();
  const [members, setMembers] = useState<OrganizationMembership[]>([]);
  const [paginatedMembers, setPaginatedMembers] = useState<OrganizationMembershipPaginationData[] | null>(null);
  const [paginationMembersMeta, setPaginationMembersMeta] = useState<ResponseMeta | null>(null);

  const getMembers = useCallback(async (organizationId: string, page: number = 1) => {
    try {
      const response = await request<APIResponse<OrganizationMembershipPaginationData[]>>(
        `/api/organizations/${organizationId}/members/?page=${page}`
      );
      if (response) {
        setPaginatedMembers(response.data);
        setPaginationMembersMeta(response.meta);
        return response;
      }
    } catch (error) {
      console.error("Failed to get organization members:", error);
      throw error;
    }
  }, [request]);

  const getCurrentMembership = useCallback(async (organizationId: string) => {
    try {
      const response = await request<APIResponse<OrganizationMembership>>(
        `/api/organizations/${organizationId}/members/me/`
      );
      return response;
    } catch (error) {
      console.error("Failed to get current user membership:", error);
      throw error;
    }
  }, [request]);

  const acceptInvite = useCallback(async (token: string) => {
    try {
      const response = await request<APIResponse<any>>(
        `/api/organizations/invites/${token}/accept/`,
        {
          method: "POST",
        }
      );

      if (response) {
        toast.success("Invitation accepted successfully");
        return response;
      }
    } catch (error) {
      console.error("Failed to accept invitation:", error);
      toast.error("Failed to accept invitation");
      throw error;
    }
  }, [request]);

  return {
    members,
    paginatedMembers,
    paginationMembersMeta,
    isLoading,
    error,
    getMembers,
    getCurrentMembership,
    acceptInvite,
  };
};

// New separate hook for organization invitations
export const useOrganizationInvitations = () => {
  const { request, isLoading, error } = useApi();
  const [invitations, setInvitations] = useState<any[]>([]);
  const [paginationMeta, setPaginationMeta] = useState<ResponseMeta | null>(null);

  const getInvitations = useCallback(async (organizationId: string, page: number = 1, filters?: {
    status?: string;
    email?: string;
  }) => {
    try {
      let url = `/api/organizations/${organizationId}/invitations/?page=${page}`;

      if (filters) {
        if (filters.status) url += `&status=${filters.status}`;
        if (filters.email) url += `&email=${encodeURIComponent(filters.email)}`;
      }

      const response = await request<APIResponse<any[]>>(url);

      if (response) {
        setInvitations(response.data || []);
        setPaginationMeta(response.meta || null);
        return response;
      }
    } catch (error) {
      console.error("Failed to get organization invitations:", error);
      throw error;
    }
  }, [request]);

  const createInvitation = useCallback(async (
    organizationId: string,
    data: {
      email: string;
      role: string;
      metadata?: Record<string, any>;
      send_email?: boolean;
    }
  ) => {
    try {
      const response = await request<APIResponse<any>>(
        `/api/organizations/${organizationId}/invitations/`,
        {
          method: "POST",
          body: JSON.stringify(data),
        }
      );

      if (response) {
        toast.success("Invitation created successfully");
        return response;
      }
    } catch (error) {
      console.error("Failed to create invitation:", error);
      toast.error("Failed to create invitation");
      throw error;
    }
  }, [request]);

  const bulkInvite = useCallback(async (
    organizationId: string,
    invitations: Array<{
      email: string;
      role: string;
      metadata?: Record<string, any>;
    }>
  ) => {
    try {
      const response = await request<APIResponse<any>>(
        `/api/organizations/${organizationId}/invitations/bulk_invite/`,
        {
          method: "POST",
          body: JSON.stringify({ invitations }),
        }
      );

      if (response) {
        const { total_created, total_failed } = response.data || { total_created: 0, total_failed: 0 };
        toast.success(`Created ${total_created} invitations. ${total_failed > 0 ? `${total_failed} failed.` : ''}`);
        return response;
      }
    } catch (error) {
      console.error("Failed to bulk invite members:", error);
      toast.error("Failed to send invitations");
      throw error;
    }
  }, [request]);

  const resendInvitation = useCallback(async (organizationId: string, invitationId: string) => {
    try {
      const response = await request<APIResponse<any>>(
        `/api/organizations/${organizationId}/invitations/${invitationId}/resend/`,
        {
          method: "POST",
        }
      );

      if (response) {
        toast.success("Invitation resent successfully");
        return response;
      }
    } catch (error) {
      console.error("Failed to resend invitation:", error);
      toast.error("Failed to resend invitation");
      throw error;
    }
  }, [request]);

  const revokeInvitation = useCallback(async (organizationId: string, invitationId: string) => {
    try {
      const response = await request<APIResponse<any>>(
        `/api/organizations/${organizationId}/invitations/${invitationId}/revoke/`,
        {
          method: "POST",
        }
      );

      if (response) {
        toast.success("Invitation revoked successfully");
        return response;
      }
    } catch (error) {
      console.error("Failed to revoke invitation:", error);
      toast.error("Failed to revoke invitation");
      throw error;
    }
  }, [request]);

  return {
    invitations,
    paginationMeta,
    isLoading,
    error,
    getInvitations,
    createInvitation,
    bulkInvite,
    resendInvitation,
    revokeInvitation,
  };
};